// Config-first deposit-rail visibility (third processor change this month — make
// the next one a config edit, not a client build).
//
// VITE_DEPOSIT_RAILS: comma-separated list of rails whose UI surfaces render.
// Recognized values: 'paypal' | 'soap'.
//
// DEFAULT (env unset): 'soap'. FAILS CLOSED, deliberately.
//
// It used to be 'paypal,soap'. NO environment sets VITE_DEPOSIT_RAILS, so every one of
// them took that default and RAIL_PAYPAL was already true everywhere -- PayPal was
// invisible only because the deposit UI additionally gated it on `!soapEnabled`. When that
// mutual exclusion was removed so all rails could show together, the default would have
// turned a live money rail on in production by omission.
//
// A money rail must never be on by default or by absence. Unset or cleared yields Soap
// only; PayPal requires someone to write 'paypal' on purpose. That also survives the
// failure this guards against -- a variable deleted months from now degrades to Soap-only
// rather than silently arming a processor.
//
// SAB-1094: NOT `??`. `??` only covers undefined/null, so a var CLEARED to '' counted as a
// real value and parsed to no rails at all -- turning off the proven Soap rail, i.e. all
// deposits. Clearing a field in the Vercel UI is how someone would try to "reset it to the
// default", so that path has to land on the default rather than on nothing.
//
// SAB-1095: the value is TRIMMED before that test, so whitespace-only ('  ', or a trailing
// newline picked up from a copy-paste or a secrets pipeline) is treated as cleared too. `||`
// alone did not cover it: '  ' is truthy, so it passed through and trimmed to no rails,
// silently taking deposits down. This codebase has already had one outage from a stray
// newline in an env var, which makes that a live failure mode rather than a hypothetical.
//
// A MIS-SET value is different and still yields no rails: 'aeropay' or a typo is truthy and
// non-blank, so it survives the trim and simply matches none of the rails. That is
// deliberate -- a wrong value is a misconfiguration to catch in review, and it cannot arm a
// processor -- but it is NOT the soft landing that unset, cleared and blank get.
//
// SAB-767: 'aeropay' is NO LONGER RECOGNIZED. Aeropay declined our application and Soap
// replaced them; the DEPOSIT UI branches and the aeropay-webhook endpoint are gone, and
// aeropay-deposit / aeropay-connect-bank / aeropay-withdraw return 410 RAIL_RETIRED.
//
// SAB-1120 CORRECTION: this comment used to claim "the client call sites ... are all
// gone". That was true of the deposit side only. The WITHDRAWAL side still called
// aeropay-connect-bank in four places (the whole Aerosync bank-linking flow) and
// aeropay-withdraw in one, and a dead handleDeposit still referenced aeropay-deposit --
// six live call sites behind a comment asserting there were none. aeropay-withdraw had
// also been DELETED rather than stubbed, so it 404'd where its siblings returned a
// deliberate 410. All six call sites are now removed and the stub restored. The lesson is
// the comment, not the code: a cleanup note that says "all gone" is a claim about a set,
// and nobody had enumerated the set.
// Setting VITE_DEPOSIT_RAILS='aeropay' now does NOTHING — there is no surface left to
// reveal. That is deliberate: a flag that appears to re-enable a rail whose code has been
// deleted is worse than no flag, because it reads as a working switch.
//
// THIS MODULE STAYS. It is the reason turning Aeropay off in production was one env var
// rather than a release, and it is the mechanism that would do the same for PayPal or Soap.
// Removing a working control because one of its inputs died would delete the lesson with
// the code.
//
// Soap remains ADDITIONALLY gated per-account by soap_access_enabled(); this
// flag can only turn its surface off globally, never widen access.
//
// Build-time baked (VITE_): changing the flag requires a redeploy, and per the
// house rule new Vercel branches need branch-scoped env set BEFORE first push.
export const DEFAULT_RAILS = 'soap'

export function parseRails(raw: string | undefined | null): Set<string> {
  return new Set(
    (raw?.trim() || DEFAULT_RAILS)
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  )
}

const enabled = parseRails(import.meta.env.VITE_DEPOSIT_RAILS as string | undefined)

export const RAIL_PAYPAL = enabled.has('paypal')
export const RAIL_SOAP = enabled.has('soap')
